import type { AssetVersionSummaryDto } from '@/api/asset'
import { parseServerDate } from '@/utils/time'

/**
 * What the version an email activation sends means for it (ORB-303). The rule is the server's — only a signed-off version
 * sends (`EmailCopyVersion.NotSendableReason`), and the save and the dispatcher both apply it. This reads the same
 * sign-off record so the activation editor and the Activations tab can say so before anyone presses Save.
 */
export interface ActivationVersionState {
    /** 0 when the activation names no version: saved before ORB-303 and missed by its conversion. It won't send. */
    versionNumber: number
    /** Null while the version isn't signed off, and the activation won't send. */
    signedOffAt: Date | null
    /** The email's latest version, when it is later than the one sent: saved, and waiting for sign-off. */
    newerVersionNumber: number | null
}

export function activationVersionState(
    versionNumber: number,
    assetVersions: AssetVersionSummaryDto[],
): ActivationVersionState {
    const sent = assetVersions.find(version => version.VersionNumber === versionNumber)
    const latest = assetVersions.reduce((highest, version) => Math.max(highest, version.VersionNumber), 0)
    return {
        versionNumber,
        signedOffAt: parseServerDate(sent?.SignedOffAt),
        newerVersionNumber: latest > versionNumber ? latest : null,
    }
}

/** "3 Oct", in the reader's locale. */
export function formatSignedOffDate(date: Date): string {
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/** "Tue 14 Oct, 09:00", in the reader's locale and time zone. */
export function formatNextSend(date: Date): string {
    return date.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
}
